import { FunctionComponent, ReactNode } from 'react';
import { Stack, SxProps, Theme, Typography } from '@mui/material';

import { Card, CardContent, HStack } from '@dw/components/core';
import { SCREEN_SIZE } from '@dw/constants';
import { classNames, componentClassName } from '@dw/utils';

import { IllustrationStage } from './IllustrationStage';

interface ISettingsSectionProps {
  icon: ReactNode;
  title: string;
  /** What this section decides, in the owner's terms — not a restatement of the title. */
  caption?: string;
  /** A status chip, a connect button: whatever belongs beside the heading rather than below it. */
  headerAction?: ReactNode;
  /**
   * A drawing of what these controls affect. Given its own tinted stage so it reads as a preview
   * rather than as live UI the owner could click — the single device that makes every illustrated
   * section in Settings look like the same idea.
   */
  illustration?: ReactNode;
  /** Puts the illustration on the left. Use when the picture is the subject and the controls annotate it. */
  illustrationFirst?: boolean;
  /**
   * Keeps the illustration in view while a long form is scrolled past it. Only worth it where the
   * form is taller than the viewport — otherwise it is motion for its own sake.
   */
  stickyIllustration?: boolean;
  /** A closing line under everything — a consequence, a fallback, a caveat. */
  note?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * Passed to the underlying Card. Exists for one real case: a section whose form has a sticky
   * footer needs `overflow: visible`, because MUI's Card clips to `hidden` and would resolve the
   * sticky against the card instead of the page.
   */
  sx?: SxProps<Theme>;
}

const cn = componentClassName('settings-section');

/**
 * One decision, with a picture of what it changes.
 *
 * This is the "Your logos" shape generalised. That card worked because the preview was the hero and
 * the controls annotated it — an owner comparing two marks is asking "is this the right image", not
 * "where is the button" — and the same is true of nearly every setting on this page. What a school
 * is really choosing is what a family sees on a phone, on an invoice or in their inbox, and none of
 * those are visible from a form field.
 *
 * Sections without an illustration are legitimate (a table of sync failures illustrates itself) and
 * simply run full width, keeping the same header so the page still reads as one hand.
 */
export const SettingsSection: FunctionComponent<ISettingsSectionProps> = (props: ISettingsSectionProps) => {

  const { icon, title, caption, headerAction, illustration, illustrationFirst, stickyIllustration, note, children, className, sx } = props;

  const controls = <Stack flex={1} minWidth={0} rowGap={2}>{children}</Stack>;

  /*
    The stage takes a real share of the row rather than shrinking to its contents. Drawings that size
    themselves (a phone, a sheet of A4) simply centre inside it; drawings that fill their width (the
    year strip, the invoice timeline) were otherwise squeezed into a sliver — a 12-month strip
    rendered 90px wide is not a smaller illustration, it is an unreadable one.
  */
  const stage = (
    <Stack flex={1} minWidth={0}>
      <IllustrationStage sticky={stickyIllustration}>{illustration}</IllustrationStage>
    </Stack>
  );

  return (
    <Card className={classNames(cn(), className)} sx={sx}>
      <CardContent>
        <Stack rowGap={2.5}>
          <HStack breakpoint={false} columnGap={1.5} justifyContent='space-between'>
            <HStack breakpoint={false} columnGap={1.25} minWidth={0}>
              {icon}
              <Stack minWidth={0}>
                <Typography variant='subtitle1' fontWeight={500} lineHeight={1.3}>{title}</Typography>
                {!!caption && <Typography variant='caption'>{caption}</Typography>}
              </Stack>
            </HStack>

            {headerAction}
          </HStack>

          {illustration ? (
            /*
              Stacks at MD rather than HStack's default XS: a phone or an A4 sheet drawn beside a
              form does not survive a tablet-width column, and a squashed preview is worse than one
              placed under the controls.
            */
            <HStack breakpoint={SCREEN_SIZE.MD} columnGap={4} rowGap={3} alignItems='flex-start'>
              {illustrationFirst && stage}
              {controls}
              {!illustrationFirst && stage}
            </HStack>
          ) : controls}

          {!!note && <Typography variant='caption'>{note}</Typography>}
        </Stack>
      </CardContent>
    </Card>
  );
};
